/**
 * FloatingTimerPill
 *
 * Always-on-top, frameless, draggable timer overlay rendered inside the Electron
 * `floating` window. Collapsed by default; expands on hover to reveal controls.
 * Receives timer state via IPC only and forwards controls to the main window.
 *
 * The window itself is resized by the main process (floating:expand / collapse);
 * this component just requests those transitions and animates its own contents.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Play, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DeepProgressRing } from '@/components/focus/DeepProgressRing';
import type { DesktopTimerState } from '@/types/electron';

// Electron's draggable-region CSS property (not in the standard typings).
const DRAG = { WebkitAppRegion: 'drag' } as React.CSSProperties;
const NO_DRAG = { WebkitAppRegion: 'no-drag' } as React.CSSProperties;

function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function FloatingTimerPill() {
  const [state, setState] = useState<DesktopTimerState | null>(null);
  const [expanded, setExpanded] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const api = window.electronAPI;
    if (!api) return;
    return api.onTimerUpdate(setState);
  }, []);

  useEffect(() => {
    return () => clearTimeout(collapseTimer.current);
  }, []);

  const api = window.electronAPI;

  const handleMouseEnter = () => {
    clearTimeout(collapseTimer.current);
    setExpanded(true);
    api?.expandFloating();
  };

  const handleMouseLeave = () => {
    collapseTimer.current = setTimeout(() => {
      setExpanded(false);
      api?.collapseFloating();
    }, 500);
  };

  if (!state) return null;

  const isActive = state.timerState === 'running' || state.timerState === 'paused';
  const isRunning = state.timerState === 'running';
  const isManager = state.timerMode === 'manager';
  const displayTime = formatClock(isManager ? state.timeRemaining : state.elapsedTime);

  const totalSeconds = state.sessionDuration * 60;
  const progress = totalSeconds > 0
    ? isManager
      ? Math.min(((totalSeconds - state.timeRemaining) / totalSeconds) * 100, 100)
      : Math.min((state.elapsedTime / totalSeconds) * 100, 100)
    : 0;

  return (
    <div
      className="glass rounded-2xl w-full h-full flex items-center overflow-hidden text-foreground"
      style={DRAG}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AnimatePresence mode="wait">
        {!expanded ? (
          <motion.div
            key="collapsed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2.5 px-3.5 w-full"
            style={DRAG}
          >
            <motion.div
              className="w-2 h-2 rounded-full bg-primary shrink-0"
              animate={{ opacity: isRunning ? [1, 0.35, 1] : 0.4 }}
              transition={{ repeat: isRunning ? Infinity : 0, duration: 2, ease: 'easeInOut' }}
            />
            {isActive ? (
              <>
                <span className="font-mono text-sm tabular-nums text-foreground/90 shrink-0">
                  {displayTime}
                </span>
                <span className="text-xs text-muted-foreground truncate flex-1">
                  {state.activeTaskName ?? 'Focus session'}
                </span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground truncate flex-1">
                Luma · no active session
              </span>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="expanded"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center w-full h-full gap-2 py-3"
            style={DRAG}
          >
            {isActive ? (
              <>
                <DeepProgressRing progress={progress} size={56} strokeWidth={2.5}>
                  <span className="text-[11px] font-mono tabular-nums font-semibold">
                    {displayTime}
                  </span>
                </DeepProgressRing>

                <p className="text-[11px] text-muted-foreground truncate max-w-[180px] text-center px-2">
                  {state.activeTaskName ?? 'Focus session'}
                </p>

                <div className="flex gap-1" style={NO_DRAG}>
                  {isRunning ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label="Pause"
                      onClick={() => api?.pauseTimer()}
                    >
                      <Pause className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label="Resume"
                      onClick={() => api?.resumeTimer()}
                    >
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive/60 hover:text-destructive"
                    aria-label="Stop"
                    onClick={() => api?.stopTimer()}
                  >
                    <Square className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-1 text-center px-2">
                <span className="text-sm font-medium">Luma</span>
                <span className="text-[11px] text-muted-foreground">No active session</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
